import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import Ico from './ui/Ico';

const BRASIL_ESTADOS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'
];

const SITUACOES = [
  { value: 'ativa',    label: <><Ico e="✅" />Ativa</> },
  { value: 'inapta',  label: <><Ico e="⚠" />Inapta</> },
  { value: 'suspensa', label: <><Ico e="🔶" />Suspensa</> },
  { value: 'baixada', label: <><Ico e="❌" />Baixada</> },
  { value: 'nula',    label: <><Ico e="⬜" />Nula</> },
];

export default function LeadExtraction({ api }) {
  const [activeSource, setActiveSource] = useState('lista_cnae');
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState([]);
  const [history, setHistory] = useState([]);
  const [limit, setLimit] = useState(50);
  const [saving, setSaving] = useState(false);

  // CNAE Principal
  const [cnaeInput, setCnaeInput] = useState('');
  const [cnaeList, setCnaeList] = useState([]);
  const [cnaeSuggestions, setCnaeSuggestions] = useState([]);

  // CNAE Secundário
  const [cnaeSecInput, setCnaeSecInput] = useState('');
  const [cnaeSecList, setCnaeSecList] = useState([]);
  const [cnaeSecSuggestions, setCnaeSecSuggestions] = useState([]);

  // Shared CNAE autocomplete data
  const [allCnaes, setAllCnaes] = useState([]);

  // Geographic filters
  const [ufSel, setUfSel] = useState('');
  const [municipioInput, setMunicipioInput] = useState('');

  // Extra Synkly filters
  const [situacao, setSituacao] = useState('');
  const [razaoSocial, setRazaoSocial] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [cnpjFiltro, setCnpjFiltro] = useState('');

  // CNPJ-source state
  const [cnpjList, setCnpjList] = useState('');

  const fetchHistory = useCallback(async () => {
    try {
      const r = await api.get('/lead-extraction/history');
      setHistory(r.data.extractions || []);
    } catch {}
  }, [api]);

  const fetchAllCnaes = useCallback(async () => {
    if (allCnaes.length > 0) return;
    try {
      const r = await api.get('/lista-cnae/cnaes');
      const raw = r.data?.cnaes ?? r.data;
      setAllCnaes(Array.isArray(raw) ? raw : []);
    } catch {}
  }, [api, allCnaes.length]);

  useEffect(() => {
    fetchHistory();
    if (activeSource === 'lista_cnae') fetchAllCnaes();
  }, [activeSource]);

  const filterCnaes = (val) => {
    if (val.length < 2) return [];
    const data = Array.isArray(allCnaes) ? allCnaes : [];
    const q = val.toLowerCase();
    return data.filter(c =>
      (c.code || c.codigo || '').toLowerCase().includes(q) ||
      (c.description || c.descricao || '').toLowerCase().includes(q)
    ).slice(0, 8);
  };

  const handleCnaeInput = (val) => {
    setCnaeInput(val);
    setCnaeSuggestions(filterCnaes(val));
  };

  const handleCnaeSecInput = (val) => {
    setCnaeSecInput(val);
    setCnaeSecSuggestions(filterCnaes(val));
  };

  const addCnae = (code) => {
    const clean = (code || '').trim();
    if (clean && !cnaeList.includes(clean) && cnaeList.length < 50)
      setCnaeList(prev => [...prev, clean]);
    setCnaeInput('');
    setCnaeSuggestions([]);
  };

  const addCnaeSec = (code) => {
    const clean = (code || '').trim();
    if (clean && !cnaeSecList.includes(clean) && cnaeSecList.length < 50)
      setCnaeSecList(prev => [...prev, clean]);
    setCnaeSecInput('');
    setCnaeSecSuggestions([]);
  };

  const handleExtract = async () => {
    setLoading(true);
    setLeads([]);
    setSelected([]);
    try {
      let payload = { limit };

      if (activeSource === 'lista_cnae') {
        const hasFilter = cnaeList.length > 0 || cnaeSecList.length > 0 ||
          ufSel || municipioInput.trim() || situacao ||
          razaoSocial.trim() || nomeFantasia.trim() || cnpjFiltro.trim();
        if (!hasFilter) {
          toast.error('Informe pelo menos um filtro antes de pesquisar (Segmento, Estado, Município, Razão Social, etc.)');
          setLoading(false);
          return;
        }

        const filters = {};
        if (cnaeList.length > 0)    filters.cnaePrincipal = cnaeList;
        if (cnaeSecList.length > 0) filters.cnaeSecundario = cnaeSecList;
        if (ufSel)                  filters.uf = ufSel;
        if (municipioInput.trim())  filters.municipio = municipioInput.trim();
        if (situacao)               filters.situacao = situacao;
        if (razaoSocial.trim())     filters.razaoSocial = razaoSocial.trim();
        if (nomeFantasia.trim())    filters.nomeFantasia = nomeFantasia.trim();
        if (cnpjFiltro.trim())      filters.cnpj = cnpjFiltro.trim();
        filters.pageSize = Math.min(limit, 100);
        payload = { extraction_type: 'synkly_cnpj', filters, limit };

      } else if (activeSource === 'cnpj') {
        const cnpjs = cnpjList.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
        if (cnpjs.length === 0) { toast.error('Informe pelo menos um CNPJ'); setLoading(false); return; }
        payload = { extraction_type: 'cnpj', filters: { cnpjs }, limit };

      } else if (activeSource === 'google_maps') {
        payload = { extraction_type: 'google_maps', query: cnaeInput, limit };
      }

      const r = await api.post('/lead-extraction', payload);
      setLeads(r.data.leads || []);
      toast.success(`${r.data.total_found} leads encontrados`);
      await fetchHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao extrair leads');
    } finally { setLoading(false); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAll = () => {
    setSelected(prev => prev.length === leads.length ? [] : leads.map(l => l.id));
  };

  const getOrCreateList = async () => {
    const r = await api.get('/prospecting/lists');
    const lists = r.data || [];
    const existing = lists.find(l => l.name === 'Lista Segmento');
    if (existing) return existing;
    const created = await api.post('/prospecting/lists', {
      name: 'Lista Segmento',
      description: 'Leads extraídos automaticamente via API Synkly CNPJ',
      tags: ['cnae', 'extração'],
    });
    return created.data;
  };

  const handleSaveToLeads = async () => {
    const toSave = leads.filter(l => selected.includes(l.id));
    if (toSave.length === 0) { toast.error('Selecione ao menos um lead'); return; }
    setSaving(true);
    try {
      // Fetch first active funnel to use as funnel_id
      const funnelRes = await api.get('/funnels/list');
      const funnels = funnelRes.data?.funnels || funnelRes.data || [];
      const activeFunnel = funnels.find(f => f.active !== false) || funnels[0];
      if (!activeFunnel) {
        toast.error('Nenhum funil encontrado. Crie um funil antes de salvar leads.');
        setSaving(false);
        return;
      }

      const prospectList = await getOrCreateList();
      let savedLeads = 0, savedProspects = 0, skippedProspects = 0, failedLeads = 0;

      for (const lead of toSave) {
        const notes = [
          lead.cnpj      ? `CNPJ: ${lead.cnpj}` : '',
          lead.address   ? `Endereço: ${lead.address}` : '',
          lead.city      ? `Cidade: ${lead.city}${lead.state ? '/' + lead.state : ''}` : '',
          lead.cnae_code ? `Segmento: ${lead.cnae_code}${lead.cnae_description ? ' - ' + lead.cnae_description : ''}` : '',
        ].filter(Boolean).join('\n');

        try {
          const leadRes = await api.post('/leads', {
            name: lead.company_name,
            email: lead.email || null,
            phone: lead.phone || null,
            company: lead.company_name,
            source: 'Synkly CNPJ',
            funnel_id: activeFunnel.id,
            notes,
            source_details: lead.cnpj ? { cnpj: lead.cnpj } : {},
          });
          try {
            await api.post(`/prospecting/lists/${prospectList.id}/prospects`, {
              name: lead.company_name, email: lead.email || '', phone: lead.phone || '',
              company: lead.company_name, cnpj: lead.cnpj || '', notes,
              source: 'synkly_cnpj', lead_id: leadRes.data?.id,
            });
            savedProspects++;
          } catch (e) { if (e?.response?.status === 409) skippedProspects++; }
          savedLeads++;
        } catch (e) {
          if (e?.response?.status === 409) {
            skippedProspects++; // conta como "já existia"
          } else {
            console.error('Erro ao salvar lead:', e?.response?.data || e);
            failedLeads++;
          }
        }
      }

      const parts = [`${savedLeads} leads salvos`];
      if (savedProspects > 0) parts.push(`${savedProspects} prospects na lista "Lista Segmento"`);
      if (skippedProspects > 0) parts.push(`${skippedProspects} já existiam`);
      if (failedLeads > 0) parts.push(`${failedLeads} falharam`);
      toast.success(`${parts.join(' · ')}`, { duration: 5000 });
      setSelected([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar leads');
    } finally { setSaving(false); }
  };

  const fmtDate = ts => { try { return new Date(ts).toLocaleDateString('pt-BR'); } catch { return ''; } };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Extração de Leads</h1>
          <p className="text-slate-500 mt-1">Extraia leads de múltiplas fontes automaticamente</p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-center">
            <p className="text-xs text-slate-500 font-medium">Leads encontrados</p>
            <p className="text-lg font-bold text-slate-700">{leads.length}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-center">
            <p className="text-xs text-slate-500 font-medium">Selecionados</p>
            <p className="text-lg font-bold text-slate-700">{selected.length}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left: params */}
        <div className="xl:col-span-2 space-y-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                Parâmetros de Busca
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Source tabs */}
              <div className="flex gap-0 border border-slate-200 rounded-lg overflow-hidden w-fit">
                {[
                  { id: 'cnpj',        label: <><Ico e="🏢" />CNPJ</> },
                  { id: 'lista_cnae',  label: '▽ Segmento' },
                  { id: 'google_maps', label: <><Ico e="📍" />Google Maps</> },
                ].map(s => (
                  <button
                    key={s.id}
                    onClick={() => setActiveSource(s.id)}
                    className={`px-5 py-2.5 text-sm font-medium transition-all ${
                      activeSource === s.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {/* ── CNAE / Synkly source ── */}
              {activeSource === 'lista_cnae' && (
                <div className="space-y-5">
                  {/* Banner */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-3">
                    <span className="text-xl"><Ico e="🔍" bare /></span>
                    <div>
                      <p className="text-sm font-semibold text-blue-900">Pesquisa via Synkly CNPJ</p>
                      <p className="text-xs text-blue-700">Combine filtros abaixo — todos são opcionais exceto quando indicado</p>
                    </div>
                  </div>

                  {/* ── CNAE Principal ── */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Códigos de Segmento <span className="font-normal text-slate-400">(até 50)</span>
                    </label>
                    <div className="relative">
                      <Input
                        placeholder="Digite o código ou descrição (ex: 4711, hipermercado…)"
                        value={cnaeInput}
                        onChange={e => handleCnaeInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') addCnae(cnaeInput); }}
                        onBlur={() => setTimeout(() => setCnaeSuggestions([]), 200)}
                        className="pr-24"
                      />
                      <button
                        onClick={() => addCnae(cnaeInput)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700"
                      >
                        + Adicionar
                      </button>
                      {cnaeSuggestions.length > 0 && (
                        <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                          {cnaeSuggestions.map((c, i) => (
                            <button
                              key={i}
                              onMouseDown={() => addCnae(c.code || c.codigo || '')}
                              className="w-full text-left px-4 py-2.5 hover:bg-blue-50 text-sm border-b border-slate-100 last:border-0"
                            >
                              <span className="font-mono font-semibold text-blue-700 mr-2">{c.code || c.codigo}</span>
                              <span className="text-slate-600">{c.description || c.descricao}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {cnaeList.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {cnaeList.map(code => (
                          <span key={code} className="flex items-center gap-1 bg-blue-100 text-blue-800 text-xs font-mono px-3 py-1 rounded-full">
                            {code}
                            <button onClick={() => setCnaeList(p => p.filter(c => c !== code))} className="text-blue-500 hover:text-blue-800 ml-0.5">×</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ── CNAE Secundário ── */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Segmento Secundário <span className="font-normal text-slate-400">(opcional — até 50)</span>
                    </label>
                    <div className="relative">
                      <Input
                        placeholder="Digite o código ou descrição"
                        value={cnaeSecInput}
                        onChange={e => handleCnaeSecInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') addCnaeSec(cnaeSecInput); }}
                        onBlur={() => setTimeout(() => setCnaeSecSuggestions([]), 200)}
                        className="pr-24"
                      />
                      <button
                        onClick={() => addCnaeSec(cnaeSecInput)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-xs bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700"
                      >
                        + Adicionar
                      </button>
                      {cnaeSecSuggestions.length > 0 && (
                        <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                          {cnaeSecSuggestions.map((c, i) => (
                            <button
                              key={i}
                              onMouseDown={() => addCnaeSec(c.code || c.codigo || '')}
                              className="w-full text-left px-4 py-2.5 hover:bg-purple-50 text-sm border-b border-slate-100 last:border-0"
                            >
                              <span className="font-mono font-semibold text-purple-700 mr-2">{c.code || c.codigo}</span>
                              <span className="text-slate-600">{c.description || c.descricao}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {cnaeSecList.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {cnaeSecList.map(code => (
                          <span key={code} className="flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-mono px-3 py-1 rounded-full">
                            {code}
                            <button onClick={() => setCnaeSecList(p => p.filter(c => c !== code))} className="text-purple-500 hover:text-purple-800 ml-0.5">×</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ── Estados ── */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Estado <span className="font-normal text-slate-400">(opcional — clique para selecionar)</span>
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {BRASIL_ESTADOS.map(uf => (
                        <button
                          key={uf}
                          onClick={() => setUfSel(prev => prev === uf ? '' : uf)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                            ufSel === uf ? 'bg-blue-600 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {uf}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* ── Município ── */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Município <span className="font-normal text-slate-400">(opcional — nome ou código IBGE)</span>
                    </label>
                    <Input
                      placeholder={ufSel ? `Ex: São Paulo, Campinas… (dentro de ${ufSel})` : 'Ex: São Paulo, Campinas…'}
                      value={municipioInput}
                      onChange={e => setMunicipioInput(e.target.value)}
                    />
                  </div>

                  {/* ── Situação Cadastral ── */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Situação Cadastral <span className="font-normal text-slate-400">(opcional)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {SITUACOES.map(s => (
                        <button
                          key={s.value}
                          onClick={() => setSituacao(prev => prev === s.value ? '' : s.value)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                            situacao === s.value
                              ? 'bg-green-600 text-white border-green-600 shadow'
                              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* ── Razão Social + Nome Fantasia ── */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Razão Social <span className="font-normal text-slate-400">(opcional)</span>
                      </label>
                      <Input
                        placeholder="Parte do nome…"
                        value={razaoSocial}
                        onChange={e => setRazaoSocial(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Nome Fantasia <span className="font-normal text-slate-400">(opcional)</span>
                      </label>
                      <Input
                        placeholder="Parte do nome…"
                        value={nomeFantasia}
                        onChange={e => setNomeFantasia(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* ── CNPJ específico ── */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      CNPJ <span className="font-normal text-slate-400">(opcional)</span>
                    </label>
                    <Input
                      placeholder="00.000.000/0000-00"
                      value={cnpjFiltro}
                      onChange={e => setCnpjFiltro(e.target.value)}
                      className="max-w-xs"
                    />
                  </div>
                </div>
              )}

              {/* ── CNPJ source ── */}
              {activeSource === 'cnpj' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">CNPJs <span className="font-normal text-slate-400">(um por linha ou separados por vírgula)</span></label>
                  <textarea
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 font-mono resize-none"
                    rows={5}
                    placeholder={"12.345.678/0001-99\n98.765.432/0001-11"}
                    value={cnpjList}
                    onChange={e => setCnpjList(e.target.value)}
                  />
                </div>
              )}

              {/* ── Google Maps source ── */}
              {activeSource === 'google_maps' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Busca no Google Maps</label>
                  <Input
                    placeholder="Ex: Restaurantes em São Paulo, Clínicas odontológicas SP…"
                    value={cnaeInput}
                    onChange={e => setCnaeInput(e.target.value)}
                  />
                </div>
              )}

              {/* Limit + Extract button */}
              <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-slate-600 whitespace-nowrap">Limite de resultados:</label>
                  <select
                    className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                    value={limit}
                    onChange={e => setLimit(Number(e.target.value))}
                  >
                    {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <Button
                  onClick={handleExtract}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 flex items-center gap-2"
                >
                  {loading ? (
                    <><span className="animate-spin"><Ico e="⏳" bare /></span> Extraindo…</>
                  ) : (
                    <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg> Extrair Leads</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Results table */}
          {leads.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{leads.length} leads encontrados</CardTitle>
                  <div className="flex items-center gap-2">
                    <button onClick={toggleAll} className="text-xs text-blue-600 hover:underline">
                      {selected.length === leads.length ? 'Desmarcar todos' : 'Selecionar todos'}
                    </button>
                    {selected.length > 0 && (
                      <Button size="sm" onClick={handleSaveToLeads} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white text-xs">
                        {saving ? <><Ico e="⏳" />Salvando…</> : <><Ico e="💾" />Salvar {selected.length} no CRM</>}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="w-10 px-4 py-3">
                          <input type="checkbox" checked={selected.length === leads.length && leads.length > 0} onChange={toggleAll} className="rounded" />
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-700">Empresa</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-700">CNPJ</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-700">Telefone</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-700">Email</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-700">Cidade/UF</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-700">Segmento</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-700">Situação</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {leads.map(lead => (
                        <tr key={lead.id} className={`hover:bg-slate-50 transition-colors ${selected.includes(lead.id) ? 'bg-blue-50' : ''}`}>
                          <td className="px-4 py-3">
                            <input type="checkbox" checked={selected.includes(lead.id)} onChange={() => toggleSelect(lead.id)} className="rounded" />
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-900">{lead.company_name}</td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-600">{lead.cnpj || '—'}</td>
                          <td className="px-4 py-3 text-slate-600">{lead.phone || '—'}</td>
                          <td className="px-4 py-3 text-slate-600">{lead.email || '—'}</td>
                          <td className="px-4 py-3 text-slate-600">{lead.city && lead.state ? `${lead.city}/${lead.state}` : lead.city || lead.state || '—'}</td>
                          <td className="px-4 py-3 text-slate-500 text-xs">
                            {lead.cnae_code ? <span className="font-mono">{lead.cnae_code}</span> : '—'}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500 capitalize">{lead.business_type || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: history */}
        <div>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Histórico de Extrações</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {history.length === 0 ? (
                <div className="px-4 py-8 text-center text-slate-400 text-sm">Nenhuma extração realizada ainda</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {history.map((h, i) => (
                    <div key={i} className="px-4 py-3 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 capitalize">{h.extraction_type?.replace(/_/g, ' ') || 'cnpj'}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{h.leads_found ?? 0} leads encontrados</p>
                          {h.filters?.cnaePrincipal?.length > 0 && (
                            <p className="text-xs font-mono text-blue-600 mt-0.5">{h.filters.cnaePrincipal.join(', ')}</p>
                          )}
                          {h.filters?.cnaes?.length > 0 && (
                            <p className="text-xs font-mono text-blue-600 mt-0.5">{h.filters.cnaes.join(', ')}</p>
                          )}
                        </div>
                        <span className="text-xs text-slate-400 whitespace-nowrap shrink-0">{fmtDate(h.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
